import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "Read With Me — daily Bible reading with Cody's verse notes & full devotional reflections",
  "Weekly Bible studies — a growing library",
  "The monthly newsletter delivered to members first",
  "Early access to new chapters before publication",
  "Direct line to Cody by email",
  "Support the ministry of The Disciple Life",
];

export default function Membership() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleSubscribe = async () => {
    if (!user) {
      navigate("/login?next=/membership");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/subscription", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="membership-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-10 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          {/* Legacy subscribers (paid once via old checkout, never renewed) —
              nudge them straight to the recurring subscription button. */}
          {user?.stripe_customer_id && !user?.stripe_subscription_id && (
            <div
              className="mb-8 p-5 rounded-md"
              style={{ background: "var(--surface-hover)", border: "1px solid var(--gold)" }}
              data-testid="membership-legacy-banner"
            >
              <div className="overline mb-1" style={{ color: "var(--gold)" }}>Auto-renewal isn't set up</div>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>
                Your original signup was billed once but your card isn't on file for renewals.
                Click <strong style={{ color: "var(--ink)" }}>Set up auto-renewal</strong> below to move onto the real monthly plan —
                your access stays uninterrupted.
              </p>
            </div>
          )}
          <div className="overline mb-4">A members-only library</div>
          <h1 className="font-serif-display text-4xl lg:text-6xl tracking-tight leading-[1.05]" style={{ color: "var(--ink)" }}>
            Join The Disciple Life Members.
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--ink-2)" }}>
            One simple tier. No locked-in contracts. Cancel any time. Every dollar goes into more writing,
            more Bible studies, and keeping this ministry independent.
          </p>

          <div className="mt-12">
            <div className="overline mb-4">What you receive</div>
            <ul className="space-y-4">
              {PERKS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-base md:text-lg" style={{ color: "var(--ink-2)" }}>
                  <Check size={20} strokeWidth={2} style={{ color: "var(--olive)", marginTop: 3, flexShrink: 0 }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="card-surface p-10 lg:p-12 sticky top-8">
            <div className="overline mb-3">Single tier</div>
            <h3 className="font-serif-display text-3xl leading-tight" style={{ color: "var(--ink)" }}>
              The Disciple Life Member
            </h3>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-serif-display text-5xl" style={{ color: "var(--ink)" }}>$4.99</span>
              <span className="text-sm" style={{ color: "var(--muted)" }}>/ month</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>
              Renews monthly. Your access begins immediately after payment.
              {user?.membership_active && (
                <span className="block mt-3 font-medium" style={{ color: "var(--olive)" }}>
                  You're already a member through {new Date(user.membership_expires_at).toLocaleDateString()}.
                </span>
              )}
            </p>
            <button
              onClick={handleSubscribe}
              disabled={busy}
              data-testid="membership-subscribe-button"
              className="btn-primary mt-8 w-full justify-center disabled:opacity-50"
            >
              {busy
                ? "Redirecting to Stripe…"
                : (user?.stripe_customer_id && !user?.stripe_subscription_id)
                  ? "Set up auto-renewal"
                  : user?.membership_active
                    ? "Extend membership"
                    : "Become a member"}
            </button>
            {!user && (
              <p className="mt-4 text-xs text-center" style={{ color: "var(--muted)" }}>
                You'll need to <Link to="/login" className="underline">sign in</Link> or <Link to="/register" className="underline">create an account</Link> first.
              </p>
            )}
            <div className="editorial-rule my-8" />
            <p className="text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
              Secure payments by Stripe. We do not store your card details. Need help? Email Cody directly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
